// src/features/procedures/components/steps/SignatureStep.jsx
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { Clear as ClearIcon, Draw as DrawIcon } from '@mui/icons-material';

/**
 * Signature Step Component
 * 
 * Options Config:
 * {
 *   "canvas_width": 500,          // Canvas width in pixels
 *   "canvas_height": 200,         // Canvas height in pixels
 *   "pen_color": "#000000",       // Pen color (hex)
 *   "pen_width": 2,               // Pen width in pixels
 *   "background_color": "#ffffff" // Background color (hex)
 * }
 */
export default function SignatureStep({ step, value, onChange, disabled, preview }) {
  const { t } = useTranslation(['procedures']);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const options = step.options || {};
  const canvasWidth = options.canvas_width || 500;
  const canvasHeight = options.canvas_height || 200;
  const penColor = options.pen_color || '#000000';
  const penWidth = options.pen_width || 2;
  const backgroundColor = options.background_color || '#ffffff';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Load existing signature if available
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = value;
    }
  }, [value, canvasWidth, canvasHeight, backgroundColor]);

  const startDrawing = (e) => {
    if (disabled || preview) return;
    
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  };

  const draw = (e) => {
    if (!isDrawing || disabled || preview) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.lineTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    ctx.stroke();
    
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      
      // Save signature as base64
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onChange(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    setHasSignature(false);
    onChange(null);
  };

  if (preview) {
    return (
      <Alert severity="info" icon={<DrawIcon />}>
        <Typography variant="body2">
          {step.label}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {t('procedures:step_options.canvas_dimensions', { width: canvasWidth, height: canvasHeight })}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
       <Paper 
        variant="outlined" 
        sx={{ 
          display: 'inline-block',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            display: 'block',
            touchAction: 'none',
          }}
        />
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ClearIcon />}
          onClick={clearSignature}
          disabled={!hasSignature || disabled}
        >
          {t('procedures:step_options.clear_signature')}
        </Button>
      </Box>
    </Box>
  );
}